import { useEffect, useState, useCallback } from "react";
import { API, apiError, formatINR, formatDateTime } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Printer, Receipt, Undo2, Search, X, AlertTriangle, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import BulkSelectBar from "../components/BulkSelectBar";

export default function SalesHistory() {
  const { user } = useAuth();
  const canBulkDelete = user?.role === "admin" || user?.role === "branch_head";
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [q, setQ] = useState("");
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returnFor, setReturnFor] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/sales", { params: { date_from: from, date_to: to, limit: 500 } });
      setSales(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected bills? Stock will be restored for unreturned items and any customer ledger entries will be removed. This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await API.post("/bulk-delete", { entity: "sales", ids: [...selected] });
      toast.success(`${data.deleted} deleted${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkBusy(false); }
  };

  const printInvoice = (id) => {
    window.open(`/invoice/${id}?auto=1`, "_blank", "width=900,height=1000");
  };

  const filtered = sales.filter((s) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      s.bill_number?.toLowerCase().includes(needle) ||
      s.customer_name?.toLowerCase().includes(needle) ||
      s.customer_phone?.toLowerCase().includes(needle) ||
      s.cashier_name?.toLowerCase().includes(needle)
    );
  });

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="sales-history-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Point of Sale</div>
          <h1 className="font-display text-3xl font-bold">Sales History</h1>
          <p className="text-sm text-slate-500 mt-1">Browse past bills, reprint invoices, or process full/partial returns with auto stock adjustment.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div>
          <div className="overline mb-1.5">From</div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="sh-from" className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <div className="overline mb-1.5">To</div>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="sh-to" className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="overline mb-1.5">Search</div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Bill no., customer, phone, cashier…"
              data-testid="sh-search"
              className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
            />
          </div>
        </div>
        <span className="text-xs text-slate-500 ml-auto self-end">{filtered.length} bills</span>
      </div>

      {canBulkDelete && (
        <BulkSelectBar
          count={selected.size}
          total={filtered.length}
          onClear={() => setSelected(new Set())}
          onSelectAll={() => setSelected(new Set(filtered.map((s) => s.id)))}
          onDelete={bulkDelete}
          busy={bulkBusy}
          entityLabel="bills"
        />
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500 text-sm">Loading sales…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-sm text-slate-500">No sales in the selected range.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  {canBulkDelete && (
                    <th className="w-8">
                      <input type="checkbox" data-testid="sh-select-all"
                        checked={filtered.length > 0 && selected.size === filtered.length}
                        onChange={(e) => setSelected(e.target.checked ? new Set(filtered.map((s) => s.id)) : new Set())} />
                    </th>
                  )}
                  <th>Date</th><th>Bill No</th><th>Customer</th>
                  <th>Cashier</th><th className="text-right">Items</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Refunded</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const refunded = parseFloat(s.refund_total || 0);
                  const status = s.status || "completed";
                  return (
                    <tr key={s.id} data-testid={`sale-row-${s.id}`} className={selected.has(s.id) ? "bg-blue-50/50" : ""}>
                      {canBulkDelete && (
                        <td>
                          <input type="checkbox" data-testid={`sh-select-${s.id}`}
                            checked={selected.has(s.id)}
                            onChange={() => setSelected((prev) => {
                              const next = new Set(prev);
                              next.has(s.id) ? next.delete(s.id) : next.add(s.id);
                              return next;
                            })} />
                        </td>
                      )}
                      <td>{formatDateTime(s.created_at)}</td>
                      <td>
                        <a href={`/invoice/${s.id}`} target="_blank" rel="noreferrer"
                           className="font-mono text-xs text-[#002FA7] font-semibold hover:underline">
                          {s.bill_number}
                        </a>
                      </td>
                      <td>
                        <div className="font-medium">{s.customer_name || "Walk-in"}</div>
                        {s.customer_phone && <div className="text-xs text-slate-500 font-mono">{s.customer_phone}</div>}
                      </td>
                      <td className="text-slate-500 text-xs">{s.cashier_name}</td>
                      <td className="text-right">{s.items?.length || 0}</td>
                      <td className="text-right font-semibold">{formatINR(s.total)}</td>
                      <td className="text-right">{refunded > 0 ? <span className="text-red-600 font-medium">− {formatINR(refunded)}</span> : "—"}</td>
                      <td>
                        <span className={`tag ${status === "completed" ? "tag-success" : status === "partial_return" ? "tag-warning" : "tag-danger"}`}>
                          {status === "partial_return" ? "Partial Return" : status === "returned" ? "Returned" : "Completed"}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => printInvoice(s.id)} data-testid={`print-${s.id}`}
                                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7]" title="Print invoice">
                            <Printer className="w-3.5 h-3.5" />
                          </button>
                          {status !== "returned" && (
                            <button onClick={() => setReturnFor(s)} data-testid={`return-${s.id}`}
                                    className="p-1.5 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-600" title="Return / Refund">
                              <Undo2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {returnFor && (
        <ReturnDialog sale={returnFor} onClose={() => setReturnFor(null)} onDone={() => { setReturnFor(null); load(); }} />
      )}
    </div>
  );
}

function ReturnDialog({ sale, onClose, onDone }) {
  // Previously returned qty per medicine
  const prevReturned = {};
  for (const r of sale.returns || []) {
    for (const it of r.items || []) {
      prevReturned[it.medicine_id] = (prevReturned[it.medicine_id] || 0) + (it.quantity || 0);
    }
  }
  const initial = (sale.items || []).map((it) => {
    const remaining = (it.quantity || 0) - (prevReturned[it.medicine_id] || 0);
    return { ...it, remaining, returnQty: 0 };
  });
  const [lines, setLines] = useState(initial);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const subtotal = sale.subtotal || (sale.items || []).reduce((s, x) => s + x.line_total, 0) || 1;
  const estRefund = lines.reduce((sum, ln) => {
    const share = (ln.line_total / subtotal) * (ln.returnQty / (ln.quantity || 1)) * (sale.total || 0);
    return sum + share;
  }, 0);

  const setQty = (id, qty) => {
    setLines((prev) => prev.map((ln) => ln.medicine_id === id
      ? { ...ln, returnQty: Math.max(0, Math.min(ln.remaining, qty || 0)) }
      : ln));
  };
  const setAll = () => setLines((prev) => prev.map((ln) => ({ ...ln, returnQty: ln.remaining })));
  const clearAll = () => setLines((prev) => prev.map((ln) => ({ ...ln, returnQty: 0 })));

  const submit = async () => {
    const items = lines.filter((ln) => ln.returnQty > 0).map((ln) => ({
      medicine_id: ln.medicine_id, quantity: ln.returnQty,
    }));
    if (items.length === 0) { toast.error("Select at least one item and quantity to return"); return; }
    if (!window.confirm(`Refund ${formatINR(estRefund)} and restore ${items.reduce((s, x) => s + x.quantity, 0)} units to stock?`)) return;
    setSaving(true);
    try {
      const { data } = await API.post(`/sales/${sale.id}/return`, { items, reason });
      toast.success(`Refunded ${formatINR(data.refund_amount)} · ${data.fully_returned ? "Full return" : "Partial return"}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const anySelected = lines.some((ln) => ln.returnQty > 0);
  const anyReturnable = lines.some((ln) => ln.remaining > 0);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="return-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="overline">Process Return</div>
            <h3 className="font-display text-xl font-semibold">{sale.bill_number}</h3>
            <div className="text-xs text-slate-500 mt-0.5">{sale.customer_name} · Bill total {formatINR(sale.total)}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          {!anyReturnable ? (
            <div className="flex items-center gap-2 p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
              <AlertTriangle className="w-4 h-4 text-slate-400" /> All items on this bill have been returned already.
            </div>
          ) : (
            <>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={setAll} data-testid="return-all" className="btn-ghost !py-1 !text-xs">Return all remaining</button>
                <button type="button" onClick={clearAll} className="btn-ghost !py-1 !text-xs">Clear</button>
              </div>
              <table className="data-table">
                <thead><tr>
                  <th>Medicine</th><th className="text-right">Sold</th>
                  <th className="text-right">Already Returned</th>
                  <th className="text-right">Remaining</th>
                  <th className="text-right">Return Qty</th>
                </tr></thead>
                <tbody>
                  {lines.map((ln) => (
                    <tr key={ln.medicine_id} data-testid={`return-line-${ln.medicine_id}`}>
                      <td>
                        <div className="font-medium">{ln.medicine_name}</div>
                        <div className="text-xs text-slate-500">Batch {ln.batch_number} · {formatINR(ln.mrp)}/unit</div>
                      </td>
                      <td className="text-right">{ln.quantity}</td>
                      <td className="text-right text-slate-500">{ln.quantity - ln.remaining}</td>
                      <td className="text-right font-medium">{ln.remaining}</td>
                      <td className="text-right">
                        {ln.remaining > 0 ? (
                          <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                            <button type="button" onClick={() => setQty(ln.medicine_id, ln.returnQty - 1)}
                                    className="p-1 hover:bg-white rounded disabled:opacity-40" disabled={ln.returnQty <= 0}>
                              <Minus className="w-3 h-3" />
                            </button>
                            <input type="number" min="0" max={ln.remaining} value={ln.returnQty}
                                   onChange={(e) => setQty(ln.medicine_id, parseInt(e.target.value))}
                                   data-testid={`return-qty-${ln.medicine_id}`}
                                   className="w-14 bg-transparent text-center font-mono text-sm outline-none" />
                            <button type="button" onClick={() => setQty(ln.medicine_id, ln.returnQty + 1)}
                                    className="p-1 hover:bg-white rounded disabled:opacity-40" disabled={ln.returnQty >= ln.remaining}>
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div>
                <div className="overline mb-1.5">Reason (optional)</div>
                <input value={reason} onChange={(e) => setReason(e.target.value)}
                       data-testid="return-reason"
                       placeholder="e.g. Customer changed mind, wrong strength, expired near batch"
                       className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
          <div className="text-sm">
            <span className="text-slate-500">Estimated refund</span>
            <span className="ml-2 font-display font-bold text-lg text-[#B91C1C]" data-testid="return-refund-preview">
              {formatINR(estRefund)}
            </span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button onClick={submit} disabled={!anySelected || saving}
                    data-testid="return-submit"
                    className="btn-primary bg-[#B91C1C] hover:bg-[#991B1B]">
              <Undo2 className="w-4 h-4" /> {saving ? "Processing…" : "Process Refund"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
